import { MapContainer, TileLayer, useMap } from "react-leaflet";
import { useState, useCallback, useMemo, memo, useEffect } from "react";
import {
    Card,
    CardContent,
    CardHeader,
    Grid,
    Typography,
    Box,
    Chip,
    Paper,
    TextField,
    CircularProgress,
} from "@mui/material";
import { MapGroup } from "./SpotsComponents/MapGroup";


// Component to automatically zoom to the area with most spots
const MapFocusEffect = memo(({ spots }) => {
    const map = useMap();
    
    useEffect(() => {
        if (!spots || spots.length === 0) return;
        
        let maxDensitySpot = spots[0];
        let maxDensity = spots[0].density || 0;
        
        for (let i = 1; i < spots.length; i++) {
            if (spots[i].density > maxDensity) {
                maxDensity = spots[i].density;
                maxDensitySpot = spots[i];
            }
        }

        const lat = Number(maxDensitySpot.latitude);
        const lng = Number(maxDensitySpot.longitude);

        if (!isNaN(lat) && !isNaN(lng)) {
            map.flyTo([lat, lng], 10, { duration: 1.5 });
        }
    }, [spots, map]);

    return null;
});

MapFocusEffect.displayName = "MapFocusEffect";


const OSM_TILE = {
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
};

/**
 * SpotsMap — Reusable density map component for Dashboards.
 *
 * @param {Object} props
 * @param {Array}  props.spots  - Array of spot data objects
 * @param {Object} props.config - Configuration object describing field mappings:
 *
 *   config.popupFields: { title, local, bairro?, logradouro? }
 *   config.filters:     [{ key, label, type: "select"|"text", allLabel? }]
 *   config.details:     { title, bairro?, localType, period: { month, year } }
 *   config.stats:       [{ label, type: "count"|"unique"|"period", field?, monthField?, yearField? }]
 *   config.tileLayer?:  "carto" (default) | "osm"
 */
export function CihaSpotsMap({ spots = [], config }) {
    const center = [-15.7801, -47.9292];

    // Build filter state dynamically from config
    const initialFilters = useMemo(() => {
        const state = {};
        config.filters.forEach((f) => {
            state[f.key] = "";
        });
        return state;
    }, [config.filters]);

    const [filters, setFilters] = useState(initialFilters);

    const handleFilterChange = useCallback((key, value) => {
        setFilters((prev) => ({ ...prev, [key]: value }));
    }, []);

    // Compute unique values for each select filter
    const filterOptions = useMemo(() => {
        const opts = {};
        const selectFilters = config.filters.filter(f => f.type === "select");
        if (selectFilters.length === 0) return opts;

        // Initialize Sets
        for (let i = 0; i < selectFilters.length; i++) {
            opts[selectFilters[i].key] = new Set();
        }

        // Single pass over spots
        for (let i = 0; i < spots.length; i++) {
            const spot = spots[i];
            for (let j = 0; j < selectFilters.length; j++) {
                const key = selectFilters[j].key;
                const val = spot[key];
                if (val != null) {
                    opts[key].add(val);
                }
            }
        }

        // Convert Sets to sorted arrays
        for (let i = 0; i < selectFilters.length; i++) {
            const key = selectFilters[i].key;
            opts[key] = Array.from(opts[key]).sort();
        }

        return opts;
    }, [spots, config.filters]);

    const [processedData, setProcessedData] = useState({ processedSpots: [], maxDensity: 1, statsData: [] });
    const [isFiltering, setIsFiltering] = useState(true);

    // Asynchronously filter spots, calculate density and statistics to avoid blocking the UI
    useEffect(() => {
        setIsFiltering(true);

        const timer = setTimeout(() => {
            if (!spots.length) {
                setProcessedData({ processedSpots: [], maxDensity: 1, statsData: [] });
                setIsFiltering(false);
                return;
            }

            // 1. Apply Filters
            const activeFilters = [];
            for (let i = 0; i < config.filters.length; i++) {
                const f = config.filters[i];
                const val = filters[f.key];
                if (val) {
                    activeFilters.push({
                        key: f.key,
                        value: val.toLowerCase()
                    });
                }
            }

            let filteredSpots = spots;
            if (activeFilters.length > 0) {
                filteredSpots = [];
                for (let i = 0; i < spots.length; i++) {
                    const spot = spots[i];
                    let match = true;
                    for (let j = 0; j < activeFilters.length; j++) {
                        const f = activeFilters[j];
                        const spotValue = spot[f.key];
                        if (!spotValue || !String(spotValue).toLowerCase().includes(f.value)) {
                            match = false;
                            break;
                        }
                    }
                    if (match) {
                        filteredSpots.push(spot);
                    }
                }
            }

            // 2. Calculate Density
            if (!filteredSpots.length) {
                // We still need to compute empty stats
                const emptyStats = config.stats.map(stat => ({
                    label: stat.label,
                    value: 0
                }));
                setProcessedData({ processedSpots: [], maxDensity: 1, statsData: emptyStats });
                setIsFiltering(false);
                return;
            }

            const radiusKm = 2;
            const latOffset = radiusKm / 111;
            const cellSize = latOffset;

            const grid = new Map();

            for (let i = 0; i < filteredSpots.length; i++) {
                const spot = filteredSpots[i];
                const lat = Number(spot.latitude);
                const lng = Number(spot.longitude);
                if (isNaN(lat) || isNaN(lng)) continue;

                const cellX = Math.floor(lng / cellSize);
                const cellY = Math.floor(lat / cellSize);
                const cellId = `${cellX}_${cellY}`;

                let cell = grid.get(cellId);
                if (!cell) {
                    cell = [];
                    grid.set(cellId, cell);
                }
                cell.push({ lat, lng });
            }

            let currentMaxDensity = 0;
            const spotsWithDensity = new Array(filteredSpots.length);
            
            for (let i = 0; i < filteredSpots.length; i++) {
                const spot = filteredSpots[i];
                const lat = Number(spot.latitude);
                const lng = Number(spot.longitude);

                if (isNaN(lat) || isNaN(lng)) {
                    spotsWithDensity[i] = { ...spot, density: 0 };
                    continue;
                }

                const cellX = Math.floor(lng / cellSize);
                const cellY = Math.floor(lat / cellSize);
                let density = 0;

                const lngOffset = radiusKm / (111 * Math.cos((lat * Math.PI) / 180));
                const latMin = lat - latOffset;
                const latMax = lat + latOffset;
                const lngMin = lng - lngOffset;
                const lngMax = lng + lngOffset;

                for (let dx = -1; dx <= 1; dx++) {
                    for (let dy = -1; dy <= 1; dy++) {
                        const neighborId = `${cellX + dx}_${cellY + dy}`;
                        const neighbors = grid.get(neighborId);
                        
                        if (neighbors) {
                            for (let j = 0; j < neighbors.length; j++) {
                                const n = neighbors[j];
                                if (
                                    n.lat >= latMin &&
                                    n.lat <= latMax &&
                                    n.lng >= lngMin &&
                                    n.lng <= lngMax
                                ) {
                                    if (n.lat === lat && n.lng === lng) {
                                        // Ignore exact duplicates of itself
                                    } else {
                                        density++;
                                    }
                                }
                            }
                        }
                    }
                }
                
                density = density + 1;

                if (density > currentMaxDensity) {
                    currentMaxDensity = density;
                }

                spotsWithDensity[i] = { ...spot, density };
            }

            // 3. Compute Stats
            const statResults = [];
            for (let i = 0; i < config.stats.length; i++) {
                const stat = config.stats[i];
                if (stat.type === "count") {
                    statResults.push({ ...stat, value: filteredSpots.length });
                } else if (stat.type === "unique" || stat.type === "period") {
                    statResults.push({ ...stat, set: new Set(), value: 0 });
                } else {
                    statResults.push({ ...stat });
                }
            }

            let needsIteration = false;
            for (let i = 0; i < statResults.length; i++) {
                if (statResults[i].set) {
                    needsIteration = true;
                    break;
                }
            }
            
            if (needsIteration) {
                for (let i = 0; i < filteredSpots.length; i++) {
                    const s = filteredSpots[i];
                    for (let j = 0; j < statResults.length; j++) {
                        const stat = statResults[j];
                        if (stat.type === "unique") {
                            stat.set.add(s[stat.field]);
                        } else if (stat.type === "period") {
                            stat.set.add(`${s[stat.monthField]}/${s[stat.yearField]}`);
                        }
                    }
                }

                for (let i = 0; i < statResults.length; i++) {
                    const stat = statResults[i];
                    if (stat.set) {
                        stat.value = stat.set.size;
                        delete stat.set;
                    }
                }
            }

            const finalStats = new Array(statResults.length);
            for (let i = 0; i < statResults.length; i++) {
                finalStats[i] = { label: statResults[i].label, value: statResults[i].value };
            }

            setProcessedData({ 
                processedSpots: spotsWithDensity, 
                maxDensity: Math.max(currentMaxDensity, 1),
                statsData: finalStats
            });
            setIsFiltering(false);

        }, 50);

        return () => clearTimeout(timer);
    }, [spots, filters, config.filters, config.stats]);

    const { processedSpots, maxDensity, statsData } = processedData;

    const [expandedCluster, setExpandedCluster] = useState(null);

    // Group spots with identical coordinates to render native exact-coordinate clusters
    const groupedSpots = useMemo(() => {
        const map = {};
        processedSpots.forEach(spot => {
            const lat = parseFloat(spot.latitude);
            const lng = parseFloat(spot.longitude);
            
            // Skip invalid or missing coordinates to prevent Leaflet crashes
            if (isNaN(lat) || isNaN(lng)) return;

            const key = `${lat}_${lng}`;
            if (!map[key]) {
                map[key] = {
                    id: key,
                    latitude: lat,
                    longitude: lng,
                    spots: [],
                    density: spot.density 
                };
            }
            map[key].spots.push(spot);
        });
        return Object.values(map);
    }, [processedSpots]);

    const handleExpandCluster = useCallback((id) => setExpandedCluster(id), []);
    const handleCloseCluster = useCallback(() => setExpandedCluster(null), []);
    const handleSelectSpot = useCallback(() => {}, []);

    const tile = OSM_TILE;

    function formatPeriod(period) {
        const monthNames = {
            1: "Janeiro",
            2: "Fevereiro",
            3: "Março",
            4: "Abril",
            5: "Maio",
            6: "Junho",
            7: "Julho",
            8: "Agosto",
            9: "Setembro",
            10: "Outubro",
            11: "Novembro",
            12: "Dezembro"
        };

        if (period?.year) {
            return `${monthNames[period.month]}/${period.year}`;
        }

        return `${monthNames[period.month]}`;
    }

    return (
        <Box
            sx={{
                display: "grid",
                gap: 2,
                width: "100%",
                minHeight: 0,
                alignItems: "start",
            }}
        >
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, minHeight: 0 }}>
    
                <Paper elevation={2} sx={{ p: 2, borderRadius: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 4, mt: 2, fontSize: "20px"}}>
                       Top 20 Municipios - Volume de Atendimentos
                    </Typography>
                    <Grid container spacing={2}>
                        {config.filters.map((f) => {
                            if (f.type === "text") {
                                return (
                                    <Grid item xs={12} sm={6} md={4} lg={3} key={f.key}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label={f.label}
                                            placeholder={f.placeholder || ""}
                                            value={filters[f.key]}
                                            onChange={(e) => handleFilterChange(f.key, e.target.value)}
                                            variant="outlined"
                                        />
                                    </Grid>
                                );
                            }

                            return (
                                <Grid item xs={12} sm={6} md={4} lg={3} key={f.key}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        select
                                        label={f.label}
                                        value={filters[f.key]}
                                        onChange={(e) => handleFilterChange(f.key, e.target.value)}
                                        variant="outlined"
                                        SelectProps={{
                                            native: true,
                                        }}
                                        InputLabelProps={{ shrink: true }}
                                    >
                                        <option value="">{f.allLabel || "Todas"}</option>
                                        {(filterOptions[f.key] || []).map((opt) => (
                                            <option key={opt} value={opt}>
                                                {opt}
                                            </option>
                                        ))}
                                    </TextField>
                                </Grid>
                            );
                        })}
                    </Grid>
                </Paper>

                <Paper elevation={2} sx={{ height: "500px", borderRadius: 2, overflow: "hidden", position: "relative" }}>
                    {isFiltering && (
                        <Box
                            sx={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                bgcolor: "rgba(255, 255, 255, 0.7)",
                                zIndex: 1000,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexDirection: "column",
                                gap: 2,
                            }}
                        >
                            <CircularProgress color="primary" />
                            <Typography variant="subtitle1" fontWeight="bold" color="textSecondary">
                                Processando pontos...
                            </Typography>
                        </Box>
                    )}
                    <MapContainer
                        center={center}
                        zoom={4}
                        className="leaflet-container-dashboards"
                        style={{ height: "100%", width: "100%", margin: 0, flex: "none" }}
                        preferCanvas={true}
                    >
                        <TileLayer attribution={tile.attribution} url={tile.url} />
                        <MapFocusEffect spots={processedSpots} />

                        {/* Highly Performant Memoized Group Rendering */}
                        {groupedSpots.map((group) => (
                            <MapGroup
                                key={group.id}
                                group={group}
                                isExpanded={expandedCluster === group.id}
                                onExpand={handleExpandCluster}
                                onClose={handleCloseCluster}
                                maxDensity={maxDensity}
                                totalSpots={processedSpots.length}
                                onSelect={handleSelectSpot}
                                config={config}
                            />
                        ))}
                    </MapContainer>
                </Paper>
            </Box>
        </Box>
    );
}
